/**
 * Where a node's material lives. A material reference names a scene `[sub_resource]`
 * or an `ExtResource` resource file, and `useMaterial` loads either into the same shape, so
 * no slot renders differently for the file a material came from. Every node type with
 * a material reference reads this one choice, since each Godot setter takes a
 * `Ref<Material>` whatever its origin.
 */
import { findExtResource, isResourceFilePath, parseResourceReference, } from '../../resources/SubResourceResolver';
import { BUILDABLE_MATERIAL_TYPES } from '../../resources/materials/buildableMaterialTypes';
import { RES_SCHEME } from '../../resources/resPath';
import { resourceFilePath } from '../../resources/subResourcePath';
import { findSubResource } from '../SceneResourcesContext';
import { NODE_PROPERTY } from '../../resources/useTexture2D';
/**
 * The source `ref` names, or undefined when it names no material. `ref` is a reference in the
 * scene's tables, or a `res://` path such as a material inside a mesh resource file. Undefined leaves the
 * slot empty, as Godot's invalid RID does, so an override that names nothing keeps what
 * the surface had. A material this previewer cannot build still fills the slot, and
 * `useMaterial` answers with Godot's default surface for it (ADR-0041). `isHeldLocally` is the
 * material's, as `MaterialResource` names it.
 */
export function resolveMaterialSource(ref, { internalResources, externalResources }, isHeldLocally = NODE_PROPERTY, origin) {
    if (!ref)
        return undefined;
    if (ref.startsWith(RES_SCHEME))
        return fileMaterialSource(ref, isHeldLocally);
    const parsed = parseResourceReference(ref);
    if (!parsed)
        return undefined;
    if (parsed.type === 'SubResource') {
        const resource = findSubResource(internalResources, parsed.id);
        if (!resource || !BUILDABLE_MATERIAL_TYPES.has(resource.type))
            return undefined;
        return {
            kind: 'inline',
            material: { resource, internalResources, externalResources, origin },
            ...held(isHeldLocally),
        };
    }
    const ext = findExtResource(externalResources, parsed.id);
    return ext?.path ? fileMaterialSource(ext.path, isHeldLocally) : undefined;
}
/**
 * One source per entry of an ArrayMesh's `materialPaths`, held as `isHeldLocally` says, as in
 * `resolveMaterialSource`. A null path is Godot's default surface.
 */
export function fileMaterialSources(materialPaths, isHeldLocally = NODE_PROPERTY) {
    return materialPaths.map((path) => (path ? fileMaterialSource(path, isHeldLocally) : undefined));
}
/**
 * The source for a material file, or undefined for a path that is no resource file.
 * Minting an address the loader must refuse buys a failed load and the same default surface.
 */
function fileMaterialSource(path, isHeldLocally) {
    return isResourceFilePath(resourceFilePath(path))
        ? { kind: 'file', path, ...held(isHeldLocally) }
        : undefined;
}
export function held(isHeldLocally) {
    return isHeldLocally ? {} : { isHeldLocally };
}
/**
 * One link of the material's `next_pass` chain, as
 * `render_forward_clustered.cpp:4189`'s `while` walk reads each link: the reference
 * resolves in the tables of the file the material is written in, and its own
 * `isHeldLocally` carries over, so the link's textures resolve in the file that owns it.
 * Undefined ends the chain, as the loop's `!material` guard does.
 *
 * @canonical One link of a material's next_pass chain
 */
export function resolveNextPass(nextPass, material) {
    const { internalResources, externalResources, isHeldLocally, origin } = material;
    return resolveMaterialSource(nextPass, { internalResources, externalResources }, isHeldLocally ?? NODE_PROPERTY, 
    // The link resolves in the same file's tables, so it keys by that file too.
    origin);
}
/**
 * The identity a visited set holds a chain source by: an inline material's resource id,
 * a file source's path. Prefixed, so an inline id and a path cannot collide. Godot
 * refuses a cyclic chain at the setter (`material.cpp:39-42`), so only a hand-written
 * file can produce one, and the renderer cuts it with this key.
 *
 * @canonical The visited-set key of a material source
 */
export function materialSourceKey(source) {
    return source.kind === 'inline'
        ? `inline:${source.material.origin ?? 'scene'}:${source.material.resource.id}`
        : `file:${source.path}`;
}
//# sourceMappingURL=materialSource.js.map